import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { faChevronDown, faChevronUp, faSearch, faTimes, faTimesCircle, faUser, faUsers, faUsersCog, } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Segmented } from "@perfectserve/library";
import { useContextSearch } from "../hooks/useContextSearch";
import { usePrivilegedAccounts } from "../hooks/usePrivilegedAccounts";
import "./ContextSelector.css";
// CSS styles are handled by the main app via CSSProvider
// This component focuses on its own specific styling
export const ContextSelector = ({ selectedAccountIds, selectedUserIds, selectedPrivilegeGroupIds, selectedAccounts: propSelectedAccounts = [], selectedUsers: propSelectedUsers = [], selectedPrivilegeGroups: propSelectedPrivilegeGroups = [], onSelectionChange, className = "", disabled = false, isPerfectServeUser: propIsPerfectServeUser, userIdentityId: propUserIdentityId, }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedPrivilegeGroups, setSelectedPrivilegeGroups] = useState([]);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);
    const resultsContainerRef = useRef(null);
    const focusedItemRefs = useRef([]);
    // Get user permissions from window if not provided via props
    const userPermissions = useMemo(() => {
        if (propIsPerfectServeUser !== undefined) {
            // If isPerfectServeUser is provided via props, we don't need to read permissions
            return undefined;
        }
        // Read from window.PSX_USER_CONFIGURATION
        const config = typeof window !== "undefined" ? window.PSX_USER_CONFIGURATION : null;
        return config?.permissions || [];
    }, [propIsPerfectServeUser]);
    // Load privileged accounts for non-PerfectServe users
    // Pass user context as parameters to avoid polling
    const { privilegedAccounts, isLoading: isLoadingPrivilegedAccounts, error: privilegedAccountsError, loadPrivilegedAccounts, isPerfectServeUser: hookIsPerfectServeUser, userIdentityId: hookUserIdentityId, permissionsLoaded, } = usePrivilegedAccounts({
        isPerfectServeUser: propIsPerfectServeUser,
        userIdentityId: propUserIdentityId,
        permissions: userPermissions,
    });
    // Use prop values if provided, otherwise use hook values
    // IMPORTANT: If props are not provided, we MUST wait for permissions to load before using hook values
    const getPerfectServeUserStatus = () => {
        if (propIsPerfectServeUser !== undefined) {
            return propIsPerfectServeUser;
        }
        if (permissionsLoaded) {
            return hookIsPerfectServeUser;
        }
        return undefined;
    };
    const isPerfectServeUser = getPerfectServeUserStatus();
    const userIdentityId = propUserIdentityId ?? hookUserIdentityId;
    const { searchText, setSearchText, accountResults, userResults, privilegeGroupResults, isSearching, searchError, activeTab, setActiveTab, loadAccountsFromGroup, } = useContextSearch(isPerfectServeUser, permissionsLoaded);
    // Auto-load privileged accounts for non-PerfectServe users
    // IMPORTANT: Only load if permissions have been loaded AND user is confirmed to NOT be a PerfectServe user
    useEffect(() => {
        // Wait for permissions to be loaded before making any decisions
        if (!permissionsLoaded) {
            return;
        }
        // Only load for confirmed non-PerfectServe users
        if (!isPerfectServeUser && userIdentityId && privilegedAccounts.length === 0 && !isLoadingPrivilegedAccounts) {
            loadPrivilegedAccounts(userIdentityId);
        }
    }, [
        isPerfectServeUser,
        userIdentityId,
        loadPrivilegedAccounts,
        privilegedAccounts.length,
        isLoadingPrivilegedAccounts,
        permissionsLoaded,
    ]);
    // No persistence needed - keep everything in memory
    // Auto-focus search input when panel opens or tab changes
    useEffect(() => {
        if (isExpanded && searchInputRef.current) {
            // Small delay to ensure the panel is rendered
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [isExpanded, activeTab]);
    // Show all search results (don't filter out selected items)
    // For non-PerfectServe users on Accounts tab, show all or filter privileged accounts by search text
    const currentResults = useMemo(() => {
        if (activeTab === "accounts") {
            // Non-PerfectServe users: use privileged accounts and filter by search text if provided
            if (!isPerfectServeUser) {
                // Show all privileged accounts if no search text
                if (!searchText.trim()) {
                    return privilegedAccounts;
                }
                // Filter by search text if provided
                const searchTerm = searchText.toLowerCase().trim();
                return privilegedAccounts.filter((account) => account.AccountName.toLowerCase().includes(searchTerm) ||
                    (account.Msa && account.Msa.toLowerCase().includes(searchTerm)));
            }
            // PerfectServe users: use API search results
            return accountResults;
        }
        else if (activeTab === "users") {
            return userResults;
        }
        else {
            return privilegeGroupResults;
        }
    }, [
        activeTab,
        accountResults,
        userResults,
        privilegeGroupResults,
        isPerfectServeUser,
        privilegedAccounts,
        searchText,
    ]);
    // Reset focused index when search results change
    useEffect(() => {
        setFocusedIndex(-1);
        // Reset the refs array when results change
        focusedItemRefs.current = [];
    }, [currentResults, activeTab]);
    // Scroll focused item into view
    useEffect(() => {
        if (focusedIndex >= 0 && focusedItemRefs.current[focusedIndex]) {
            const focusedElement = focusedItemRefs.current[focusedIndex];
            if (focusedElement && resultsContainerRef.current) {
                focusedElement.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "nearest",
                });
            }
        }
    }, [focusedIndex]);
    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsExpanded(false);
            }
        };
        if (isExpanded) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isExpanded]);
    // Initialize internal state from props when they change
    useEffect(() => {
        // Use full objects if available, otherwise create placeholders from IDs
        if (selectedAccountIds.length > 0) {
            let accountsToSet;
            if (propSelectedAccounts.length > 0) {
                // Use full objects when available - filter to match the selected IDs
                accountsToSet = propSelectedAccounts.filter((acc) => selectedAccountIds.includes(acc.Id));
                // Add placeholders for any missing accounts
                const existingIds = new Set(accountsToSet.map((acc) => acc.Id));
                const missingIds = selectedAccountIds.filter((id) => !existingIds.has(id));
                const placeholders = missingIds.map((id) => ({
                    Id: id,
                    AccountName: `Account ${id}`,
                    AccountType: "Unknown",
                    Msa: "",
                }));
                accountsToSet = [...accountsToSet, ...placeholders];
            }
            else {
                // Create placeholder objects from IDs
                accountsToSet = selectedAccountIds.map((id) => ({
                    Id: id,
                    AccountName: `Account ${id}`, // Placeholder name
                    AccountType: "Unknown",
                    Msa: "",
                }));
            }
            // Only update if the accounts have actually changed to avoid infinite loops
            const currentIds = selectedAccounts.map((acc) => acc.Id).sort();
            const newIds = accountsToSet.map((acc) => acc.Id).sort();
            const idsChanged = JSON.stringify(currentIds) !== JSON.stringify(newIds);
            if (idsChanged) {
                setSelectedAccounts(accountsToSet);
            }
        }
        else if (selectedAccounts.length > 0) {
            setSelectedAccounts([]);
        }
    }, [selectedAccountIds, propSelectedAccounts]);
    // Initialize internal state from props for users
    useEffect(() => {
        // Use full objects if available, otherwise create placeholders from IDs
        if (selectedUserIds.length > 0) {
            let usersToSet;
            if (propSelectedUsers.length > 0) {
                // Use full objects when available - filter to match the selected IDs
                usersToSet = propSelectedUsers.filter((user) => selectedUserIds.includes(user.Id));
                // Add placeholders for any missing users
                const existingIds = new Set(usersToSet.map((user) => user.Id));
                const missingIds = selectedUserIds.filter((id) => !existingIds.has(id));
                const placeholders = missingIds.map((id) => ({
                    Id: id,
                    DisplayName: `User ${id}`,
                    FirstName: "Unknown",
                    LastName: "User",
                    AccountName: "Unknown Account",
                    Msa: "",
                    Stage: "Active",
                }));
                usersToSet = [...usersToSet, ...placeholders];
            }
            else {
                // Create placeholder objects from IDs
                usersToSet = selectedUserIds.map((id) => ({
                    Id: id,
                    DisplayName: `User ${id}`, // Placeholder name
                    FirstName: "Unknown",
                    LastName: "User",
                    AccountName: "Unknown Account",
                    Msa: "",
                    Stage: "Active",
                }));
            }
            // Only update if the users have actually changed to avoid infinite loops
            const currentIds = selectedUsers.map((user) => user.Id).sort();
            const newIds = usersToSet.map((user) => user.Id).sort();
            const idsChanged = JSON.stringify(currentIds) !== JSON.stringify(newIds);
            if (idsChanged) {
                setSelectedUsers(usersToSet);
            }
        }
        else if (selectedUsers.length > 0) {
            setSelectedUsers([]);
        }
    }, [selectedUserIds, propSelectedUsers]);
    // Initialize internal state from props for privilege groups
    useEffect(() => {
        // Use full objects if available, otherwise create placeholders from IDs
        if (selectedPrivilegeGroupIds.length > 0) {
            let groupsToSet;
            if (propSelectedPrivilegeGroups.length > 0) {
                // Use full objects when available - filter to match the selected IDs
                groupsToSet = propSelectedPrivilegeGroups.filter((group) => selectedPrivilegeGroupIds.includes(group.GroupId));
                // Add placeholders for any missing groups
                const existingIds = new Set(groupsToSet.map((group) => group.GroupId));
                const missingIds = selectedPrivilegeGroupIds.filter((id) => !existingIds.has(id));
                const placeholders = missingIds.map((id) => ({
                    GroupId: id,
                    Name: `Group ${id}`,
                }));
                groupsToSet = [...groupsToSet, ...placeholders];
            }
            else {
                // Create placeholder objects from IDs
                groupsToSet = selectedPrivilegeGroupIds.map((id) => ({
                    GroupId: id,
                    Name: `Group ${id}`, // Placeholder name
                }));
            }
            // Only update if the groups have actually changed to avoid infinite loops
            const currentIds = selectedPrivilegeGroups.map((group) => group.GroupId).sort();
            const newIds = groupsToSet.map((group) => group.GroupId).sort();
            const idsChanged = JSON.stringify(currentIds) !== JSON.stringify(newIds);
            if (idsChanged) {
                setSelectedPrivilegeGroups(groupsToSet);
            }
        }
        else if (selectedPrivilegeGroups.length > 0) {
            setSelectedPrivilegeGroups([]);
        }
    }, [selectedPrivilegeGroupIds, propSelectedPrivilegeGroups]);
    // Keyboard navigation handlers
    const handleKeyDown = useCallback((event) => {
        if (!isExpanded)
            return;
        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                event.stopPropagation();
                if (currentResults.length > 0) {
                    setFocusedIndex((prev) => {
                        const nextIndex = prev < currentResults.length - 1 ? prev + 1 : 0;
                        return nextIndex;
                    });
                }
                break;
            case "ArrowUp":
                event.preventDefault();
                event.stopPropagation();
                if (currentResults.length > 0) {
                    setFocusedIndex((prev) => {
                        const nextIndex = prev > 0 ? prev - 1 : currentResults.length - 1;
                        return nextIndex;
                    });
                }
                break;
            case "Enter":
                event.preventDefault();
                event.stopPropagation();
                if (focusedIndex >= 0 && focusedIndex < currentResults.length) {
                    const item = currentResults[focusedIndex];
                    if (activeTab === "accounts") {
                        handleSelectAccount(item, true);
                    }
                    else if (activeTab === "users") {
                        handleSelectUser(item, true);
                    }
                    else if (activeTab === "privilegeGroups") {
                        handleSelectPrivilegeGroup(item, true);
                    }
                }
                break;
            case "Escape":
                event.preventDefault();
                event.stopPropagation();
                setIsExpanded(false);
                break;
        }
    }, [isExpanded, currentResults, focusedIndex, activeTab]);
    // Notify parent of selection changes
    const notifySelectionChange = (accounts, users, groups) => {
        const selection = {
            accountIds: accounts.map((a) => a.Id),
            userIds: users.map((u) => u.Id),
            privilegeGroupIds: groups.map((g) => g.GroupId),
            accounts,
            users,
            privilegeGroups: groups,
        };
        onSelectionChange(selection);
    };
    const handleSelectAccount = (account, keepOpen = false) => {
        // Check against current internal state, not props
        const isCurrentlySelected = selectedAccounts.some((acc) => acc.Id === account.Id);
        if (isCurrentlySelected) {
            // Deselect if already selected
            const newAccounts = selectedAccounts.filter((acc) => acc.Id !== account.Id);
            setSelectedAccounts(newAccounts);
            notifySelectionChange(newAccounts, selectedUsers, selectedPrivilegeGroups);
        }
        else {
            // Add to existing selections, replacing any placeholder with the full object
            const existingWithoutPlaceholder = selectedAccounts.filter((acc) => acc.Id !== account.Id);
            const newAccounts = [...existingWithoutPlaceholder, account];
            setSelectedAccounts(newAccounts);
            notifySelectionChange(newAccounts, selectedUsers, selectedPrivilegeGroups);
        }
        if (!keepOpen) {
            setSearchText("");
        }
    };
    const handleSelectUser = (user, keepOpen = false) => {
        // Check against current internal state, not props
        const isCurrentlySelected = selectedUsers.some((u) => u.Id === user.Id);
        if (isCurrentlySelected) {
            // Deselect if already selected
            const newUsers = selectedUsers.filter((u) => u.Id !== user.Id);
            setSelectedUsers(newUsers);
            notifySelectionChange(selectedAccounts, newUsers, selectedPrivilegeGroups);
        }
        else {
            // Add to existing selections, replacing any placeholder with the full object
            const existingWithoutPlaceholder = selectedUsers.filter((u) => u.Id !== user.Id);
            const newUsers = [...existingWithoutPlaceholder, user];
            setSelectedUsers(newUsers);
            notifySelectionChange(selectedAccounts, newUsers, selectedPrivilegeGroups);
        }
        if (!keepOpen) {
            setSearchText("");
        }
    };
    const handleSelectPrivilegeGroup = async (group, keepOpen = false) => {
        // Check against current internal state, not props
        const isCurrentlySelected = selectedPrivilegeGroups.some((g) => g.GroupId === group.GroupId);
        if (isCurrentlySelected) {
            // Deselect if already selected
            const newGroups = selectedPrivilegeGroups.filter((g) => g.GroupId !== group.GroupId);
            setSelectedPrivilegeGroups(newGroups);
            notifySelectionChange(selectedAccounts, selectedUsers, newGroups);
        }
        else {
            // Add privilege group to existing selections, replacing any placeholder with the full object
            const existingWithoutPlaceholder = selectedPrivilegeGroups.filter((g) => g.GroupId !== group.GroupId);
            const newGroups = [...existingWithoutPlaceholder, group];
            setSelectedPrivilegeGroups(newGroups);
            try {
                // Load accounts associated with this privilege group
                const groupAccounts = await loadAccountsFromGroup(group.GroupId);
                // Add unique accounts to existing account selections
                const existingAccountIds = new Set(selectedAccounts.map((acc) => acc.Id));
                const newAccounts = groupAccounts.filter((acc) => !existingAccountIds.has(acc.Id));
                const updatedAccounts = [...selectedAccounts, ...newAccounts];
                setSelectedAccounts(updatedAccounts);
                notifySelectionChange(updatedAccounts, selectedUsers, newGroups);
            }
            catch (_error) {
                // Silently handle privilege group account loading errors
                // Still add the group even if account loading fails
                notifySelectionChange(selectedAccounts, selectedUsers, newGroups);
            }
        }
        if (!keepOpen) {
            setSearchText("");
        }
    };
    const handleRemoveAccount = (accountId) => {
        const newAccounts = selectedAccounts.filter((acc) => acc.Id !== accountId);
        setSelectedAccounts(newAccounts);
        notifySelectionChange(newAccounts, selectedUsers, selectedPrivilegeGroups);
    };
    const handleRemoveUser = (userId) => {
        const newUsers = selectedUsers.filter((user) => user.Id !== userId);
        setSelectedUsers(newUsers);
        notifySelectionChange(selectedAccounts, newUsers, selectedPrivilegeGroups);
    };
    const handleRemovePrivilegeGroup = (groupId) => {
        const newGroups = selectedPrivilegeGroups.filter((group) => group.GroupId !== groupId);
        setSelectedPrivilegeGroups(newGroups);
        notifySelectionChange(selectedAccounts, selectedUsers, newGroups);
    };
    // Select All Accounts functionality
    const handleSelectAllAccounts = () => {
        // For non-PerfectServe users, use privileged accounts; for PerfectServe users, use search results
        const accountsToSelect = !isPerfectServeUser ? privilegedAccounts : accountResults;
        // Get all account results that are not already selected
        const unselectedAccounts = accountsToSelect.filter((account) => !selectedAccounts.some((selected) => selected.Id === account.Id));
        // Add all unselected accounts to the current selection
        const newAccounts = [...selectedAccounts, ...unselectedAccounts];
        setSelectedAccounts(newAccounts);
        notifySelectionChange(newAccounts, selectedUsers, selectedPrivilegeGroups);
    };
    // Check if all current account results are selected
    const areAllAccountResultsSelected = useMemo(() => {
        if (activeTab !== "accounts")
            return false;
        const accountsToCheck = !isPerfectServeUser ? privilegedAccounts : accountResults;
        if (accountsToCheck.length === 0)
            return false;
        return accountsToCheck.every((account) => selectedAccounts.some((selected) => selected.Id === account.Id));
    }, [accountResults, privilegedAccounts, selectedAccounts, activeTab, isPerfectServeUser]);
    const getUserStageClassName = (stage) => {
        switch (stage) {
            case "Active":
                return "ps-context-selector__user-stage--active";
            case "InActive":
                return "ps-context-selector__user-stage--inactive";
            case "InProgress":
                return "ps-context-selector__user-stage--inprogress";
            default:
                return "";
        }
    };
    // Helper function to check if an item is selected (check against internal state)
    const isItemSelected = (item) => {
        if (activeTab === "accounts") {
            return selectedAccounts.some((acc) => acc.Id === item.Id);
        }
        else if (activeTab === "users") {
            return selectedUsers.some((user) => user.Id === item.Id);
        }
        else {
            return selectedPrivilegeGroups.some((group) => group.GroupId === item.GroupId);
        }
    };
    const totalSelected = selectedAccountIds.length + selectedUserIds.length + selectedPrivilegeGroupIds.length;
    const _selectedObjects = [...selectedAccounts, ...selectedUsers];
    const _getDisplayName = (obj) => {
        if ("AccountName" in obj && "AccountType" in obj)
            return obj.AccountName;
        if ("DisplayName" in obj && "Stage" in obj)
            return obj.DisplayName;
        return "Unknown";
    };
    return (_jsxs("div", { ref: containerRef, className: `ps-context-selector ${className}`.trim(), children: [_jsxs("div", { className: `ps-context-selector__header ${disabled ? "ps-context-selector__header--disabled" : ""}`, onClick: () => !disabled && setIsExpanded(!isExpanded), children: [_jsxs("div", { className: "ps-context-selector__header-content", children: [_jsx(FontAwesomeIcon, { icon: faUsers, style: { color: "#6c757d", fontSize: "14px" } }), _jsx("span", { className: "ps-context-selector__header-text", children: totalSelected > 0 ? `${totalSelected} Selected Context${totalSelected > 1 ? "s" : ""}` : "Select Context" })] }), _jsx(FontAwesomeIcon, { icon: isExpanded ? faChevronUp : faChevronDown, style: { color: "#6c757d", fontSize: "12px" } })] }), isExpanded && (_jsxs("div", { className: "ps-context-selector__panel", children: [_jsx("div", { className: "ps-context-selector__tabs", children: _jsx(Segmented, { name: "context-selector-tabs", value: activeTab, onChange: (value) => {
                                setActiveTab(value);
                                // Focus search input after tab change
                                setTimeout(() => searchInputRef.current?.focus(), 50);
                            }, options: isPerfectServeUser
                                ? [
                                    {
                                        value: "accounts",
                                        label: "Accounts",
                                        icon: faUsers,
                                    },
                                    {
                                        value: "users",
                                        label: "Users",
                                        icon: faUser,
                                    },
                                    {
                                        value: "privilegeGroups",
                                        label: "Groups",
                                        icon: faUsersCog,
                                    },
                                ]
                                : [
                                    {
                                        value: "accounts",
                                        label: "Accounts",
                                        icon: faUsers,
                                    },
                                    {
                                        value: "users",
                                        label: "Users",
                                        icon: faUser,
                                    },
                                ], size: "medium", fullWidth: true }) }), _jsx("div", { className: "ps-context-selector__search", children: _jsxs("div", { className: "ps-context-selector__search-wrapper", children: [_jsx(FontAwesomeIcon, { icon: faSearch, className: "ps-context-selector__search-icon" }), _jsx("input", { ref: searchInputRef, type: "text", value: searchText, onChange: (e) => setSearchText(e.target.value), onKeyDown: handleKeyDown, placeholder: `Search ${activeTab}...`, className: "ps-context-selector__search-input", onClick: (e) => e.stopPropagation() }), searchText && (_jsx("button", { onClick: (e) => {
                                        e.stopPropagation();
                                        setSearchText("");
                                    }, className: "ps-context-selector__search-clear", children: _jsx(FontAwesomeIcon, { icon: faTimes }) }))] }) }), _jsx("div", { ref: resultsContainerRef, className: "ps-context-selector__results", onKeyDown: handleKeyDown, tabIndex: -1, children: !permissionsLoaded && activeTab === "accounts" ? (_jsx("div", { className: "ps-context-selector__empty-state", children: "Loading permissions..." })) : !isPerfectServeUser && activeTab === "accounts" && privilegedAccountsError ? (_jsx("div", { className: "ps-context-selector__empty-state", style: { color: "#dc3545" }, children: privilegedAccountsError })) : !isPerfectServeUser && activeTab === "accounts" && isLoadingPrivilegedAccounts ? (_jsx("div", { className: "ps-context-selector__empty-state", children: "Loading your accounts..." })) : !searchText && isPerfectServeUser ? (_jsxs("div", { className: "ps-context-selector__empty-state", children: ["Start typing to search ", activeTab, "..."] })) : isSearching ? (_jsx("div", { className: "ps-context-selector__empty-state", children: "Searching..." })) : searchError ? (_jsx("div", { className: "ps-context-selector__empty-state", style: { color: "#dc3545" }, children: searchError })) : currentResults.length === 0 ? (_jsx("div", { className: "ps-context-selector__empty-state", children: !isPerfectServeUser && activeTab === "accounts"
                                ? "No matching accounts found"
                                : `No ${activeTab} found` })) : (_jsxs("div", { children: [activeTab === "accounts" &&
                                    ((!isPerfectServeUser && privilegedAccounts.length > 0) ||
                                        (isPerfectServeUser && accountResults.length > 0)) && (_jsxs("div", { className: "ps-context-selector__select-all-option", style: {
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        padding: "8px 12px",
                                        borderBottom: "1px solid #e9ecef",
                                        marginBottom: "4px",
                                        cursor: "pointer",
                                        backgroundColor: "#f8f9fa",
                                        fontWeight: "500",
                                    }, onClick: (e) => {
                                        e.stopPropagation();
                                        handleSelectAllAccounts();
                                    }, children: [_jsx("div", { style: {
                                                padding: "1px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }, children: _jsx("input", { type: "checkbox", checked: areAllAccountResultsSelected, onChange: () => { }, style: {
                                                    cursor: "pointer",
                                                    pointerEvents: "none", // Prevent double handling
                                                } }) }), _jsx("div", { style: { flex: 1, cursor: "pointer" }, children: _jsxs("div", { className: "ps-context-selector__result-title", children: ["Select All Accounts (", !isPerfectServeUser ? privilegedAccounts.length : accountResults.length, ")"] }) })] })), currentResults.map((item, index) => {
                                    const isSelected = isItemSelected(item);
                                    const isFocused = index === focusedIndex;
                                    return (_jsxs("div", { ref: (el) => {
                                            focusedItemRefs.current[index] = el;
                                        }, className: `ps-context-selector__result-item ${"Stage" in item ? getUserStageClassName(item.Stage) : ""} ${isFocused ? "ps-context-selector__result-item--focused" : ""}`, style: {
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: "8px",
                                            cursor: "default",
                                            backgroundColor: isSelected ? "#e7f3ff" : isFocused ? "#f8f9fa" : "transparent",
                                        }, onMouseEnter: () => setFocusedIndex(index), onMouseMove: () => {
                                            // Only update focus if it's different to avoid unnecessary updates
                                            if (focusedIndex !== index) {
                                                setFocusedIndex(index);
                                            }
                                        }, children: [_jsx("div", { style: {
                                                    padding: "1px",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }, onClick: (e) => {
                                                    e.stopPropagation();
                                                    if (activeTab === "accounts") {
                                                        handleSelectAccount(item, true);
                                                    }
                                                    else if (activeTab === "users") {
                                                        handleSelectUser(item, true);
                                                    }
                                                    else if (activeTab === "privilegeGroups") {
                                                        handleSelectPrivilegeGroup(item, true);
                                                    }
                                                }, children: _jsx("input", { type: "checkbox", checked: isSelected, onChange: () => { }, style: {
                                                        cursor: "pointer",
                                                        pointerEvents: "none", // Prevent double handling
                                                    } }) }), _jsx("div", { style: {
                                                    flex: 1,
                                                    cursor: "pointer",
                                                }, onClick: (e) => {
                                                    e.stopPropagation();
                                                    if (activeTab === "accounts") {
                                                        handleSelectAccount(item, true);
                                                    }
                                                    else if (activeTab === "users") {
                                                        handleSelectUser(item, true);
                                                    }
                                                    else if (activeTab === "privilegeGroups") {
                                                        handleSelectPrivilegeGroup(item, true);
                                                    }
                                                }, children: "DisplayName" in item ? (
                                                // User item
                                                _jsxs("div", { children: [_jsx("div", { className: "ps-context-selector__result-title", children: item.DisplayName }), _jsxs("div", { className: "ps-context-selector__result-subtitle", children: [item.AccountName, " \u2022 ", item.Msa] })] })) : "Name" in item ? (
                                                // Privilege Group item
                                                _jsxs("div", { children: [_jsx("div", { className: "ps-context-selector__result-title", children: item.Name }), _jsx("div", { className: "ps-context-selector__result-subtitle", children: item.GroupId })] })) : "AccountName" in item ? (
                                                // Account item
                                                _jsxs("div", { style: {
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "flex-start",
                                                        width: "100%",
                                                    }, children: [_jsx("div", { children: _jsx("div", { className: "ps-context-selector__result-title", children: item.AccountName }) }), _jsx("div", { className: "ps-context-selector__result-subtitle", style: {
                                                                fontSize: "10px",
                                                                textAlign: "right",
                                                                marginLeft: "8px",
                                                            }, children: item.Msa })] })) : (_jsx("div", { className: "ps-context-selector__result-title", children: "Unknown Item" })) })] }, "Id" in item ? item.Id : "GroupId" in item ? item.GroupId : "unknown"));
                                })] })) }), (selectedAccounts.length > 0 || selectedUsers.length > 0 || selectedPrivilegeGroups.length > 0) && (_jsxs("div", { className: "ps-context-selector__selected-section", children: [_jsxs("div", { className: "ps-context-selector__selected-header", children: [_jsxs("span", { children: ["Selected Items (", selectedAccounts.length + selectedUsers.length + selectedPrivilegeGroups.length, ")"] }), _jsx("button", { className: "ps-context-selector__clear-all-button", onClick: (e) => {
                                            e.stopPropagation();
                                            setSelectedAccounts([]);
                                            setSelectedUsers([]);
                                            setSelectedPrivilegeGroups([]);
                                            notifySelectionChange([], [], []);
                                        }, children: "Clear All" })] }), _jsxs("div", { className: "ps-context-selector__selected-items", children: [selectedAccounts.map((account) => (_jsxs("div", { className: "ps-context-selector__selected-item", children: [_jsx("div", { className: "ps-context-selector__selected-item-content", children: _jsx("div", { className: "ps-context-selector__selected-item-title", children: account.AccountName }) }), _jsx("button", { className: "ps-context-selector__remove-button", onClick: (e) => {
                                                    e.stopPropagation();
                                                    handleRemoveAccount(account.Id);
                                                }, title: "Remove account", children: _jsx(FontAwesomeIcon, { icon: faTimesCircle }) })] }, account.Id))), selectedUsers.map((user) => (_jsxs("div", { className: "ps-context-selector__selected-item", children: [_jsxs("div", { className: "ps-context-selector__selected-item-content", children: [_jsx("div", { className: "ps-context-selector__selected-item-title", children: user.DisplayName }), _jsxs("div", { className: "ps-context-selector__selected-item-subtitle", children: ["User \u2022 ", user.AccountName] })] }), _jsx("button", { className: "ps-context-selector__remove-button", onClick: (e) => {
                                                    e.stopPropagation();
                                                    handleRemoveUser(user.Id);
                                                }, title: "Remove user", children: _jsx(FontAwesomeIcon, { icon: faTimesCircle }) })] }, user.Id))), selectedPrivilegeGroups.map((group) => (_jsxs("div", { className: "ps-context-selector__selected-item", children: [_jsxs("div", { className: "ps-context-selector__selected-item-content", children: [_jsx("div", { className: "ps-context-selector__selected-item-title", children: group.Name }), _jsxs("div", { className: "ps-context-selector__selected-item-subtitle", children: ["Group \u2022 ", group.GroupId] })] }), _jsx("button", { className: "ps-context-selector__remove-button", onClick: (e) => {
                                                    e.stopPropagation();
                                                    handleRemovePrivilegeGroup(group.GroupId);
                                                }, title: "Remove group", children: _jsx(FontAwesomeIcon, { icon: faTimesCircle }) })] }, group.GroupId)))] })] }))] }))] }));
};
